import { useRef, useState } from 'react';
import { Calendar } from 'lucide-react';

/** Parses various date formats and returns YYYY-MM-DD */
export function parseDateInput(input: string): string {
  const trimmed = input.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  const match = trimmed.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
  if (match) {
    const [, day, month, year] = match;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  return trimmed;
}

/** Prüft nicht nur das Format (YYYY-MM-DD), sondern ob das Datum real existiert — sonst
 *  wurden Tippfehler wie "14.15.2027" (Monat 15) klaglos als gültiges ISO-Datum gespeichert
 *  (Kundenfeedback 16.10.2026). new Date() rechnet Überläufe sonst automatisch um (z.B. Monat
 *  13 → Januar des Folgejahres) — das Round-Trip-Check deckt genau das auf. */
export function isValidIsoDate(iso: string): boolean {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return false;
  const year = Number(m[1]), month = Number(m[2]), day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  const dt = new Date(year, month - 1, day);
  return dt.getFullYear() === year && dt.getMonth() === month - 1 && dt.getDate() === day;
}

/** Formats YYYY-MM-DD to dd.mm.yyyy for display */
export function formatDateDisplay(isoDate: string): string {
  if (!isoDate) return '';
  const match = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) return `${match[3]}.${match[2]}.${match[1]}`;
  return isoDate;
}

interface DateTextInputProps {
  value: string;
  onChange: (isoDate: string) => void;
  placeholder?: string;
  darkMode: boolean;
  className?: string;
}

/** Combined text + date-picker input: allows paste/type AND calendar picker */
export function DateTextInput({ value, onChange, placeholder, darkMode, className }: DateTextInputProps) {
  const dateRef = useRef<HTMLInputElement>(null);
  // Während des Tippens NICHT bei jedem Tastendruck neu formatieren/padden (padStart) — das
  // ließ den Cursor springen und einzelne Ziffern beim Ändern einer einzelnen Stelle
  // "verschwinden"/auf 0 springen (Kundenfeedback 16.10.2026). Rohtext bleibt während des
  // Fokus frei editierbar, erst beim Verlassen des Felds wird geparst/formatiert/committed.
  const [draft, setDraft] = useState<string | null>(null);
  const displayValue = draft !== null ? draft : formatDateDisplay(value);

  const baseClass = className || `w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${
    darkMode ? 'bg-slate-600 border-slate-500 text-white placeholder-slate-400' : 'bg-white border-slate-300'
  }`;

  return (
    <div className="relative">
      <input
        type="text"
        value={displayValue}
        onFocus={() => setDraft(formatDateDisplay(value))}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={(e) => {
          const raw = e.target.value.trim();
          if (raw) {
            const parsed = parseDateInput(raw);
            if (isValidIsoDate(parsed)) onChange(parsed);
          } else {
            onChange('');
          }
          setDraft(null);
        }}
        placeholder={placeholder || 'tt.mm.jjjj'}
        className={baseClass}
        style={{ paddingRight: '2.5rem' }}
      />
      <input
        ref={dateRef}
        type="date"
        value={/^\d{4}-\d{2}-\d{2}$/.test(value) ? value : ''}
        onChange={(e) => { onChange(e.target.value); setDraft(null); }}
        className="absolute inset-0 opacity-0 pointer-events-none"
        tabIndex={-1}
      />
      <button
        type="button"
        onClick={() => dateRef.current?.showPicker?.()}
        className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded transition-colors ${
          darkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-400 hover:text-slate-600'
        }`}
      >
        <Calendar className="w-4 h-4" />
      </button>
    </div>
  );
}
